import { rem } from 'polished'
import React, { useCallback } from 'react'
import styled from 'styled-components'

import CuratedTripsIntroStep from './CuratedTripsIntroStep'

import {
  tripLoginModalSignUpDismiss,
  tripLoginModalSignUpView,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import TextButton from 'components/Luxkit/Button/TextButton'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import usePendingLoginHandler from 'hooks/usePendingLoginHandler'
import noop from 'lib/function/noop'
import CreateTrip from 'tripPlanner/components/TripModal/Trips/CreateTrip/CreateTripTP'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'

const Sections = styled.div`
  margin-top: ${rem(32)};
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    align-items: stretch;
  }
`

const Section = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  flex: auto;
  padding: ${rem(20)};
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
`

const CuratedTripSection = styled(Section)`
  ${mediaQueryUp.tablet} {
    flex: 3;
  }
`

const CustomTripSection = styled(Section)`
  ${mediaQueryUp.tablet} {
    flex: 2;
  }

  ${mediaQueryUp.desktop} {
    flex: 1;
  }
`

const CuratedSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};

  ${mediaQueryUp.desktop} {
    flex-direction: row;

    & > * {
      flex: 1;
    }
  }
`

const BottomButtons = styled(Group)`
  flex: 1;
`

const Or = styled(Heading)`
  align-self: center;
`

const curatedSteps = [
  {
    heading: 'Select a trip you like',
    body: 'Each trip is carefully curated by experts for different destinations and themes.',
  },
  {
    heading: 'Make it your own',
    body: 'Mix and match items or copy the whole itinerary. Planning made fast and easy.',
  },
  {
    heading: 'Book & enjoy',
    body: 'Book everything in one place or add in items booked elsewhere.',
  },
]

interface Props {
  onClickBrowse?: () => void
}

const trackTripLoginModalSignUpView = () => {
  fireInteractionEvent(tripLoginModalSignUpView('create_trip'))
}
const trackTripLoginModalSignUpDismiss = () => {
  fireInteractionEvent(tripLoginModalSignUpDismiss('create_trip'))
}

function CuratedTripsIntro({ onClickBrowse = noop }: Props) {
  const openModal = useOpenModal()

  const openCreateTrip = useCallback(() => openModal(CreateTrip), [openModal])

  const onCreateClick = usePendingLoginHandler(
    openCreateTrip,
    'tripPlannerLogin',
    trackTripLoginModalSignUpView,
    trackTripLoginModalSignUpDismiss,
  )

  return (
    <>
      <Heading variant="heading2">Create your dream trip, your way!</Heading>
      <Sections>
        <CuratedTripSection>
          <Heading variant="heading4">Use a curated trip</Heading>
          <CuratedSteps>
            {curatedSteps.map((step, i) => (
              <CuratedTripsIntroStep
                key={i}
                stepNumber={i + 1}
                heading={step.heading}
                body={step.body}
              />
            ))}
          </CuratedSteps>
          <BottomButtons
            direction="horizontal"
            verticalAlign="end"
            horizontalAlign="end"
          >
            <TextButton kind="tertiary" onClick={onClickBrowse}>
              Browse curated trips
            </TextButton>
          </BottomButtons>
        </CuratedTripSection>
        <Or variant="heading6">or</Or>
        <CustomTripSection>
          <Heading variant="heading4">Build your own trip</Heading>
          <BodyText variant="medium">
            Use our TripPlanner tool to organise every part of your journey. No
            matter where or who you booked with.
          </BodyText>
          <BottomButtons
            direction="horizontal"
            verticalAlign="end"
            horizontalAlign="end"
          >
            <TextButton
              kind="primary"
              variant="default"
              onClick={onCreateClick}
            >
              Create new trip
            </TextButton>
          </BottomButtons>
        </CustomTripSection>
      </Sections>
    </>
  )
}

export default CuratedTripsIntro
